import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { authClient } from "@/lib/auth/client";
import * as React from "react";

type GoogleSignInButtonProps = {
  callbackURL: string;
  label: string;
  disabled?: boolean;
  requestSignUp?: boolean;
  onError: (message: string | null) => void;
};

function GoogleIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" className="size-4">
      <path fill="#4285F4" d="M21.8 12.2c0-.7-.1-1.3-.2-1.9H12v3.6h5.5a4.7 4.7 0 0 1-2 3.1v2.6h3.2c1.9-1.8 3.1-4.4 3.1-7.4Z" />
      <path fill="#34A853" d="M12 22c2.7 0 5-0.9 6.7-2.4L15.5 17c-.9.6-2 .9-3.5.9-2.6 0-4.8-1.8-5.6-4.1H3.1v2.6A10 10 0 0 0 12 22Z" />
      <path fill="#FBBC05" d="M6.4 13.8a6 6 0 0 1 0-3.6V7.6H3.1a10 10 0 0 0 0 8.8l3.3-2.6Z" />
      <path fill="#EA4335" d="M12 6.1c1.5 0 2.8.5 3.8 1.5l2.9-2.9A9.7 9.7 0 0 0 12 2a10 10 0 0 0-8.9 5.6l3.3 2.6C7.2 7.9 9.4 6.1 12 6.1Z" />
    </svg>
  );
}

export function GoogleSignInButton({ callbackURL, label, disabled, requestSignUp, onError }: GoogleSignInButtonProps) {
  const [loading, setLoading] = React.useState(false);

  const signInWithGoogle = async () => {
    setLoading(true);
    onError(null);

    try {
      const result = await authClient.signIn.social({
        provider: "google",
        callbackURL,
        newUserCallbackURL: callbackURL,
        errorCallbackURL: typeof window !== "undefined" ? `${window.location.pathname}${window.location.search}` : "/auth/login",
        requestSignUp,
      });

      if (result?.error) {
        onError(result.error.message ?? "Unable to continue with Google.");
        setLoading(false);
      }
    } catch (error) {
      onError(error instanceof Error ? error.message : "Unable to continue with Google.");
      setLoading(false);
    }
  };

  return (
    <Button
      type="button"
      variant="outline"
      className="w-full gap-2 active:scale-[0.96] transition-transform"
      disabled={disabled || loading}
      onClick={() => void signInWithGoogle()}
    >
      {loading ? <Spinner size="sm" /> : <GoogleIcon />}
      <span translate="no">{loading ? "Redirecting..." : label}</span>
    </Button>
  );
}
